import { Platform } from "react-native";
import * as SecureStore from "expo-secure-store";
import type { PartnerInviteContext } from "./types";

const PARTNER_INVITE_STORAGE_KEY = "louise.partner_invite";
const PARTNER_INVITE_STORAGE_VERSION = 1;
const PARTNER_INVITE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_INVITATION_CODE_LENGTH = 512;

type StoredPartnerInvite = {
  version: typeof PARTNER_INVITE_STORAGE_VERSION;
  code: string;
  receivedAt: number;
};

function getSessionStorage(): Storage | null {
  if (Platform.OS !== "web" || typeof window === "undefined") return null;
  return window.sessionStorage;
}

function normalizeCode(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const code = value.trim();
  if (!code || code.length > MAX_INVITATION_CODE_LENGTH || /\s/.test(code)) {
    return null;
  }
  return code;
}

function parseInvite(stored: string): PartnerInviteContext | null {
  try {
    const parsed: unknown = JSON.parse(stored);
    if (!parsed || typeof parsed !== "object") return null;

    const candidate = parsed as {
      version?: unknown;
      code?: unknown;
      receivedAt?: unknown;
    };
    const code = normalizeCode(candidate.code);
    if (!code) return null;

    // Before the envelope was versioned, invite data could contain an
    // untrusted inviterName. Migrate only the opaque code and start a fresh
    // local TTL; never carry display metadata forward from the URL/storage.
    const receivedAt =
      candidate.version === PARTNER_INVITE_STORAGE_VERSION &&
      typeof candidate.receivedAt === "number"
        ? candidate.receivedAt
        : Date.now();
    if (!Number.isFinite(receivedAt)) return null;

    const age = Date.now() - receivedAt;
    if (age < -5 * 60 * 1000 || age > PARTNER_INVITE_TTL_MS) return null;

    return { code, receivedAt };
  } catch {
    return null;
  }
}

export async function savePartnerInvite(
  invite: PartnerInviteContext,
): Promise<void> {
  const code = normalizeCode(invite.code);
  if (!code) {
    await clearPartnerInvite();
    return;
  }

  const receivedAt = Number.isFinite(invite.receivedAt)
    ? invite.receivedAt
    : Date.now();
  const stored: StoredPartnerInvite = {
    version: PARTNER_INVITE_STORAGE_VERSION,
    code,
    receivedAt,
  };
  const serialized = JSON.stringify(stored);
  const sessionStorage = getSessionStorage();
  if (sessionStorage) {
    sessionStorage.setItem(PARTNER_INVITE_STORAGE_KEY, serialized);
    return;
  }

  await SecureStore.setItemAsync(PARTNER_INVITE_STORAGE_KEY, serialized, {
    keychainAccessible: SecureStore.WHEN_UNLOCKED_THIS_DEVICE_ONLY,
  });
}

export async function loadPartnerInvite(): Promise<PartnerInviteContext | null> {
  const sessionStorage = getSessionStorage();
  if (sessionStorage) {
    const stored = sessionStorage.getItem(PARTNER_INVITE_STORAGE_KEY);
    if (!stored) return null;
    const invite = parseInvite(stored);
    if (!invite) sessionStorage.removeItem(PARTNER_INVITE_STORAGE_KEY);
    return invite;
  }

  const stored = await SecureStore.getItemAsync(PARTNER_INVITE_STORAGE_KEY);
  if (!stored) return null;
  const invite = parseInvite(stored);
  if (!invite) await SecureStore.deleteItemAsync(PARTNER_INVITE_STORAGE_KEY);
  return invite;
}

export async function clearPartnerInvite(): Promise<void> {
  const sessionStorage = getSessionStorage();
  if (sessionStorage) {
    sessionStorage.removeItem(PARTNER_INVITE_STORAGE_KEY);
    return;
  }

  await SecureStore.deleteItemAsync(PARTNER_INVITE_STORAGE_KEY);
}
